import { Input } from 'antd';
import React, { forwardRef, useEffect, useId, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../theme/semanticTokens';
import { Typography } from '../../Typography';
import {
  InputClearAction,
  type InputFieldBaseProps,
  inputFieldBorderStyle,
  InputHelpText,
  InputLabel
} from '../_shared/InputField';

export type PromoCodeInputProps = InputFieldBaseProps &
  Partial<{
    onApply: () => void;
    applyDisabled: boolean;
  }>;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ForwardedInput = forwardRef<HTMLInputElement, any>((props, ref) => (
  <Input {...props} ref={ref} />
));

const StyledInput = styled(ForwardedInput)<{
  $error: boolean;
  $disabled: boolean;
  $readOnly: boolean;
}>`
  &&.ant-input-affix-wrapper {
    ${inputFieldBorderStyle}
    border-radius: ${theme.dime.radius.m};
    padding: 0 ${theme.dime.spacing.l};
    height: ${theme.dime.spacing['3xl']};
    box-shadow: none;
    margin-bottom: ${theme.dime.spacing.m};
  }

  &&.ant-input-affix-wrapper-disabled {
    cursor: not-allowed;
  }

  &&.ant-input-affix-wrapper-readonly {
    cursor: not-allowed;
  }

  && .ant-input {
    text-transform: uppercase;
    background-color: transparent;
  }

  && .ant-input::placeholder {
    text-transform: none;
  }

  &&.ant-input-affix-wrapper-disabled .ant-input::placeholder {
    color: ${theme.dime.colors.text.disabled};
  }
`;

const ApplyButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const ApplyLabel = styled(Typography)<{ $active: boolean }>`
  color: ${({ $active }) =>
    $active
      ? theme.dime.colors.link.primary
      : theme.dime.colors.link.primaryDisabled};
`;

export const PromoCodeInput = forwardRef<HTMLInputElement, PromoCodeInputProps>(
  (
    {
      value,
      defaultValue,
      placeholder = 'Enter promo code',
      disabled = false,
      readOnly = false,
      onChange,
      onFocus,
      onBlur,
      className = '',
      id,
      name,
      status = 'default',
      label,
      optional = false,
      infoIcon = false,
      infoText,
      helpText,
      clearAction = false,
      onClear,
      onApply,
      applyDisabled,
      'aria-labelledby': ariaLabelledBy,
      ...rest
    },
    ref
  ) => {
    const hasError = status === 'error';
    const [currentValue, setCurrentValue] = useState(
      value ?? defaultValue ?? ''
    );
    const hasValue = currentValue !== '' && currentValue !== undefined;
    const isApplyActive = hasValue && !hasError && !disabled && !readOnly;
    const isApplyButtonDisabled = applyDisabled ?? !isApplyActive;
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const labelId = `${inputId}-label`;

    useEffect(() => {
      if (value !== undefined) setCurrentValue(value);
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value === undefined) setCurrentValue(e.target.value);
      onChange?.(e);
    };

    return (
      <InputWrapper className={className}>
        {!!label && (
          <InputLabel
            id={labelId}
            label={label}
            optional={optional}
            infoIcon={infoIcon}
            infoText={infoText}
          />
        )}
        <StyledInput
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          id={inputId}
          aria-labelledby={label ? labelId : ariaLabelledBy}
          name={name}
          suffix={
            <ApplyButton
              type="button"
              onClick={onApply}
              disabled={isApplyButtonDisabled}
            >
              <ApplyLabel variant="body1" $active={!isApplyButtonDisabled}>
                Apply
              </ApplyLabel>
            </ApplyButton>
          }
          $error={hasError}
          $disabled={disabled}
          $readOnly={readOnly}
          {...rest}
        />
        {!!helpText && <InputHelpText text={helpText} hasError={hasError} />}
        {!!clearAction && !disabled && !readOnly && (
          <InputClearAction onClear={onClear} />
        )}
      </InputWrapper>
    );
  }
);

PromoCodeInput.displayName = 'PromoCodeInput';
